import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './TaskManagement.css';

function TaskManagement({ currentUser }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    schedule_cron: '',
    is_enabled: true
  });

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  useEffect(() => {
    if (isAdmin) {
      fetchTasks();
    }
  }, [isAdmin]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/admin/tasks');
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setError('Failed to load tasks. Please try again later.');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchExecutions = async (taskId) => {
    try {
      const response = await api.get(`/api/admin/tasks/${taskId}/executions`);
      setExecutions(response.data.executions || []);
    } catch (error) {
      console.error('Error fetching executions:', error);
    }
  };

  const handleRunTask = async (taskName) => {
    try {
      setError(null);
      await api.post(`/api/admin/tasks/${taskName}/run`);
      alert(`Task "${taskName}" started in background. Check execution history for progress.`);
      setTimeout(() => fetchTasks(), 2000);
    } catch (error) {
      console.error('Error running task:', error);
      setError(`Failed to run task: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleScheduleTask = async (taskId) => {
    try {
      setError(null);
      await api.put(`/api/admin/tasks/${taskId}/schedule`, scheduleForm);
      alert('Task schedule updated successfully');
      setShowScheduleModal(false);
      fetchTasks();
    } catch (error) {
      console.error('Error updating schedule:', error);
      setError(`Failed to update schedule: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleViewExecutions = (task) => {
    setSelectedTask(task);
    fetchExecutions(task.id);
  };

  const handleCleanupStuckTasks = async () => {
    if (!window.confirm('This will mark all stuck/running tasks (running for over 1 hour) as failed. Continue?')) {
      return;
    }
    try {
      setError(null);
      const response = await api.post('/api/admin/tasks/cleanup-stuck');
      alert(response.data.message || 'Cleanup completed successfully');
      fetchTasks();
    } catch (error) {
      console.error('Error cleaning up stuck tasks:', error);
      setError(`Failed to cleanup: ${error.response?.data?.error || error.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#16a34a';
      case 'failed':
        return '#dc2626';
      case 'running':
        return '#2563eb';
      default:
        return '#6b7280';
    }
  };

  const formatCron = (cron) => {
    if (!cron) return 'Not scheduled';
    const parts = cron.split(' ');
    if (parts.length === 5) {
      return `Every ${parts[2]} of month at ${parts[1]}:${parts[0]} (day ${parts[4]})`;
    }
    return cron;
  };

  if (!isAdmin) {
    return (
      <div className="task-management">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page. Only administrators can manage scheduled tasks.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="task-management">
        <div className="loading">Loading tasks...</div>
      </div>
    );
  }

  return (
    <div className="task-management">
      <div className="task-header">
        <div>
          <h1>Scheduled Tasks</h1>
          <p>Manage background tasks and schedules</p>
        </div>
        <button
          onClick={handleCleanupStuckTasks}
          className="btn btn-secondary"
          style={{ marginTop: '10px' }}
        >
          Cleanup Stuck Tasks
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="tasks-list">
        {tasks.length === 0 ? (
          <div className="no-tasks">
            <p>No scheduled tasks found. Tasks will appear here once created.</p>
          </div>
        ) : (
          tasks.map((task) => (
            <div key={task.id} className="task-card">
              <div className="task-card-header">
                <h3>{task.task_name}</h3>
                <span className={`task-type ${task.task_type}`}>{task.task_type}</span>
              </div>
              
              <p className="task-description">{task.description}</p>
              
              <div className="task-info">
                <div className="info-item">
                  <strong>Schedule:</strong> {formatCron(task.schedule_cron)}
                </div>
                <div className="info-item">
                  <strong>Status:</strong>
                  <span 
                    className="status-badge" 
                    style={{ backgroundColor: getStatusColor(task.last_run_status) }}
                  >
                    {task.is_enabled ? (task.last_run_status || 'pending') : 'disabled'}
                  </span>
                </div>
                <div className="info-item">
                  <strong>Last Run:</strong> {task.last_run_at ? new Date(task.last_run_at).toLocaleString('en-GB') : 'Never'}
                </div>
                <div className="info-item">
                  <strong>Run Count:</strong> {task.run_count || 0}
                </div>
                {task.last_run_message && (
                  <div className="info-item">
                    <strong>Last Message:</strong> {task.last_run_message}
                  </div>
                )}
              </div>
              
              <div className="task-actions">
                <button
                  onClick={() => handleRunTask(task.task_name)}
                  className="btn btn-primary"
                  disabled={task.last_run_status === 'running'}
                >
                  {task.last_run_status === 'running' ? 'Running...' : 'Run Now'}
                </button>
                <button
                  onClick={() => {
                    setScheduleForm({
                      schedule_cron: task.schedule_cron || '0 * * * *',
                      is_enabled: task.is_enabled
                    });
                    setShowScheduleModal(true);
                    setSelectedTask(task);
                  }}
                  className="btn btn-secondary"
                >
                  Schedule
                </button>
                <button
                  onClick={() => handleViewExecutions(task)}
                  className="btn btn-secondary"
                >
                  View History
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showScheduleModal && selectedTask && (
        <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Schedule Task: {selectedTask.task_name}</h2>
              <button className="close-button" onClick={() => setShowScheduleModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Cron Expression</label>
                <input
                  type="text"
                  value={scheduleForm.schedule_cron}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, schedule_cron: e.target.value })}
                  placeholder="0 * * * * (minute hour day month day_of_week)"
                />
                <small>
                  Examples: "0 * * * *" (hourly), "0 0 * * *" (daily), "0 0 * * 0" (weekly on Sunday)
                </small>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={scheduleForm.is_enabled}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, is_enabled: e.target.checked })}
                  />
                  Enable scheduled runs
                </label>
              </div>
              <div className="modal-actions">
                <button
                  onClick={() => handleScheduleTask(selectedTask.id)}
                  className="btn btn-primary"
                >
                  Save Schedule
                </button>
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedTask && executions.length > 0 && (
        <div className="executions-modal">
          <div className="modal-overlay" onClick={() => setSelectedTask(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Execution History: {selectedTask.task_name}</h2>
                <button className="close-button" onClick={() => setSelectedTask(null)}>×</button>
              </div>
              <div className="modal-body">
                <table className="executions-table">
                  <thead>
                    <tr>
                      <th>Started</th>
                      <th>Completed</th>
                      <th>Status</th>
                      <th>Duration</th>
                      <th>Records</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {executions.map((exec) => (
                      <tr key={exec.id}>
                        <td>{exec.started_at ? new Date(exec.started_at).toLocaleString('en-GB') : '-'}</td>
                        <td>{exec.completed_at ? new Date(exec.completed_at).toLocaleString('en-GB') : '-'}</td>
                        <td>
                          <span 
                            className="status-badge" 
                            style={{ backgroundColor: getStatusColor(exec.status) }}
                          >
                            {exec.status}
                          </span>
                        </td>
                        <td>{exec.execution_time_ms ? `${exec.execution_time_ms}ms` : '-'}</td>
                        <td>{exec.records_processed || '-'}</td>
                        <td>{exec.message || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskManagement;

